import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MessageSquareQuote, Quote, Play } from 'lucide-react';
import StarRating from '../components/testimonials/StarRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function WallCard({ testimonial }) {
    const [playing, setPlaying] = useState(false);

    const textAnswer = testimonial.answers
        ? Object.values(testimonial.answers).find(v => typeof v === 'string' && v.length > 10)
        : null;

    return (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-md transition-shadow">
            {/* Video */}
            {testimonial.video_url && (
                <div className="relative bg-stone-900 aspect-video">
                    {playing ? (
                        <video src={testimonial.video_url} autoPlay controls playsInline
                            className="w-full h-full object-cover" onEnded={() => setPlaying(false)} />
                    ) : (
                        <button onClick={() => setPlaying(true)}
                            className="w-full h-full flex items-center justify-center group">
                            {testimonial.thumbnail_url ? (
                                <img src={testimonial.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <div className="text-white/30"><Play className="w-12 h-12" /></div>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/20 transition-colors">
                                <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                                    <Play className="w-6 h-6 text-stone-900 ml-0.5" />
                                </div>
                            </div>
                        </button>
                    )}
                </div>
            )}

            <div className="p-4 space-y-3">
                {/* Rating */}
                {testimonial.overall_rating > 0 && (
                    <StarRating value={testimonial.overall_rating} maxStars={5} size="sm" readonly />
                )}

                {/* Quote */}
                {textAnswer && (
                    <div className="relative">
                        <Quote className="w-5 h-5 text-stone-200 absolute -top-0.5 -left-0.5" />
                        <p className="text-sm text-stone-600 leading-relaxed pl-4 italic line-clamp-4">"{textAnswer}"</p>
                    </div>
                )}

                {/* Attribution */}
                <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
                    <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-xs font-medium text-stone-600">
                        {(testimonial.contact_name || '?').charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-stone-800">{testimonial.contact_name}</span>
                </div>
            </div>
        </div>
    );
}

export default function TestimonialWall() {
    const { orgSlug } = useParams();
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/testimonials/public/wall/${orgSlug}`);
                setTestimonials(Array.isArray(res.data) ? res.data : res.data.data || []);
            } catch {
                setError('Testimonial wall not found');
            } finally { setLoading(false); }
        })();
    }, [orgSlug]);

    if (loading) return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
    );

    if (error) return (
        <div className="min-h-screen bg-stone-50 flex items-center justify-center">
            <div className="text-center">
                <MessageSquareQuote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-sm text-stone-500">{error}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-stone-50 py-12 px-4">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-10">
                    <h1 className="text-3xl font-bold text-stone-900">What Our Customers Say</h1>
                    <p className="text-stone-500 mt-2">Real feedback from real people</p>
                </div>

                {testimonials.length === 0 ? (
                    <div className="text-center py-16">
                        <MessageSquareQuote className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                        <p className="text-sm text-stone-500">No testimonials yet</p>
                    </div>
                ) : (
                    <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                        {testimonials.map(t => (
                            <div key={t.submission_id} className="break-inside-avoid">
                                <WallCard testimonial={t} />
                            </div>
                        ))}
                    </div>
                )}

                <div className="text-center mt-10 text-xs text-stone-400">
                    Powered by <span className="font-medium">NurtureCRM</span>
                </div>
            </div>
        </div>
    );
}
